import { honestRollUp } from './estimate.js'

// The roll-up bar. Three honesty rules, all visible, none behind a tooltip.
//
// 🔴 HANDOFF: "All three are always visible, never behind a tooltip." There is
// no disclosure control in this component and adding one would be the defect,
// not a refinement — the rules exist because a cost figure without them is a
// claim the board cannot support, and a claim you have to hover to check is
// still a claim.
//
// 🔴 ONE FIGURE PER CURRENCY. Never a sum across them. The list comes from
// `RollUpRead.subtotals`, which has no total field "so the shape does not offer
// one", and nothing in this component reduces it. A trip in ¥ and £ shows two
// figures, each labeled — and, since T10, a converted figure ALONGSIDE them
// with its as-of date.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE CONVERTED FIGURE IS A SEPARATE ELEMENT, NOT A REPLACEMENT
// ═══════════════════════════════════════════════════════════════════════════
//
// The tempting render is to collapse the list into the one number now that a
// legitimate one exists. `utils/cost.py` ruled that out before this component
// did: *"it adds a converted figure alongside these … it does not replace
// them, and it does not get to delete this list."* Two reasons it matters:
//
//   * the per-currency figures are what people TYPED. The converted one is
//     arithmetic over a third party's daily rate, and when the two disagree
//     it is the typed ones that are right about what somebody was quoted.
//   * conversion can be refused — a currency ECB does not carry, a feed that
//     is down — and a screen that had replaced the list would then have
//     nothing at all to show.
//
// ⚠️ AND THE DATE IS NOT OPTIONAL. TR7: *"conversions labeled with their as-of
// date."* The figure and its date are rendered by ONE expression below, so
// there is no branch in which the number appears without it.
//
// ⚠️ ENRICHMENT IS A SECOND REQUEST AND MAY SIMPLY NOT HAVE ARRIVED. Every
// path through this component works with `conversion` absent — that is the
// board's state for the first moment of every page load, and it is the
// permanent state whenever a provider is down. §6: *"Decoration may fail; the
// grid may not."*

export default function Estimate({ estimate, headcount, conversion }) {
  const rollUp = estimate?.overall
  const {
    headcount: headcountText,
    coverage,
    figures,
    notes,
    conversion: converted,
    currency: currencyText,
  } = honestRollUp(rollUp, headcount, conversion)

  return (
    <section className="d2d-rollup" aria-label="What this is going to cost" data-testid="estimate">
      <div className="d2d-rollup__figures">
        <p className="d2d-rollup__eyebrow d2d-micro">Roughly, each</p>
        {figures.length > 0 ? (
          <ul className="d2d-rollup__list">
            {figures.map((figure) => (
              <li key={figure.currency} className="d2d-rollup__figure">
                <span className="d2d-rollup__amount">≈ {figure.each}</span>
                {/* The code is rendered beside every figure, always — not only
                    when there are two. On a single-currency trip it is one word
                    of redundancy; the alternative is a figure whose unit is
                    implied, and "implied" is what added ¥ to £. */}
                <span className="d2d-rollup__currency d2d-micro">{figure.currency}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="d2d-rollup__amount d2d-rollup__amount--none">no amounts yet</p>
        )}

        {/* 🔴 The figure and its date, in ONE element. There is no branch here
            in which a converted number reaches the screen without the date the
            rate is from — TR7, and it is the only thing that makes a single
            cross-currency figure legitimate at all. `stale` is §8: *"last-known
            rates used, as-of date shown — a visibly old date is honest."* */}
        {converted?.state === 'ok' && converted.figure && figures.length > 1 ? (
          <p className="d2d-rollup__converted" data-testid="converted-figure">
            <span className="d2d-rollup__amount">≈ {converted.figure}</span>{' '}
            <span className="d2d-rollup__asof d2d-micro">
              all in{converted.asOf ? ` · ${converted.asOf} rates` : ''}
              {converted.stale ? ' · latest we have' : ''}
            </span>
          </p>
        ) : null}
      </div>

      <ul className="d2d-rollup__rules">
        {headcountText ? <li data-testid="honesty-headcount">{headcountText}</li> : null}
        <li data-testid="honesty-coverage">{coverage}</li>
        {currencyText ? (
          // 🔴 THE GUARD MOVED RATHER THAN DISAPPEARING. This line used to read
          // *"N currencies, kept apart — nothing is converted yet"*, hardcoded
          // here, and it became FALSE the moment conversion shipped. Deleting
          // it would have left two figures side by side with nothing saying
          // what the relationship between them is, which is the exact state the
          // original line was written to prevent.
          //
          // `estimate.js::currencyLine` now writes one of three sentences —
          // converted, refused-with-a-reason, or nothing at all on a
          // single-currency trip — and which one is right depends on data this
          // component should not be inspecting.
          <li data-testid="honesty-currency">{currencyText}</li>
        ) : null}
        {notes.map((note) => (
          <li key={note.key} data-testid={`honesty-basis-${note.rule}`}>
            {note.text}
          </li>
        ))}
      </ul>
    </section>
  )
}
